import { Button, Divider, Stack, type SxProps, type Theme } from '@mui/material';
import { ArrowRightOutlined } from '@mui/icons-material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { Mode } from '@/types/entity-mode';
import { Publish } from '@/components/events/detail/tabs/flows/grid/actions/publish';

type FlowModeSectionContext = 'flowDetail' | 'flowGrid' | 'detailsOverview' | 'flowDetailSchedule';

export type FlowModeSectionProps = {
  currentMode: Mode;
  hasPlanMode: boolean;
  hasOrganizeMode: boolean;
  handleModeChange: (mode: Mode) => void;
  flow: FlowDto | null;
  planFlowOverride?: FlowDto | null;
  eventSlotIdParam?: string | null;
  onShowPublishAlert?: () => void;
  context?: FlowModeSectionContext;
  showDivider?: boolean;
  sx?: SxProps<Theme>;
  onClick?: (e: React.MouseEvent) => void;
};

export const FlowModeSection = ({
  currentMode,
  hasPlanMode,
  hasOrganizeMode,
  handleModeChange,
  flow,
  planFlowOverride,
  eventSlotIdParam: _eventSlotIdParam = null,
  onShowPublishAlert,
  context = 'flowDetail',
  showDivider = true,
  sx,
  onClick,
}: FlowModeSectionProps) => {
  // eventSlotIdParam reserved for future use
  void _eventSlotIdParam;
  return (
    <>
      {showDivider && <Divider orientation="horizontal" />}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0}
        sx={{
          height: '32px',
          minHeight: '32px',
          maxHeight: '32px',
          p: 0,
          width: '100%',
          ...sx,
        }}
        onClick={onClick || ((e) => e.stopPropagation())}
      >
        {/* PLAN mode section - only show if PLAN mode data is available */}
        {hasPlanMode ? (
          <>
            {/* PLAN mode button */}
            <Button
              variant={currentMode === 'PLAN' ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              onClick={() => handleModeChange('PLAN')}
              disabled={!hasPlanMode}
              sx={{ minWidth: 88 }}
            >
              DRAFT
            </Button>
            {/* ArrowRightOutlined */}
            <ArrowRightOutlined fontSize="small" color="action" />
            {/* Publish button for flow */}
            {flow ? (
              <Publish
                flow={flow}
                buttonOrIcon="icon"
                context={context === 'detailsOverview' || context === 'flowDetailSchedule' ? 'flowDetail' : context}
                flowPlanOverride={planFlowOverride}
                onIndicativeClick={onShowPublishAlert}
              />
            ) : null}
            {/* ArrowRightOutlined */}
            <ArrowRightOutlined fontSize="small" color="action" />
          </>
        ) : null}
        {/* ORGANIZE mode button */}
        <Button
          variant={currentMode === 'ORGANIZE' ? 'contained' : 'outlined'}
          color="secondary"
          size="small"
          onClick={() => handleModeChange('ORGANIZE')}
          disabled={!hasOrganizeMode}
          sx={{ minWidth: 88 }}
        >
          PUBLISHED
        </Button>
      </Stack>
    </>
  );
};
